import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, Proposal, UserSubscription } from '../../models/types';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, MessageSquare, CreditCard, Calendar, Eye, MapPin, Send, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ChatModal from '../../components/Chat/ChatModal';

interface ProposalWithProject extends Proposal {
  project: Project;
  hasRequesterMessage: boolean;
}

const ProviderDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [ongoingProjects, setOngoingProjects] = useState<ProposalWithProject[]>([]);
  const [closedProjects, setClosedProjects] = useState<ProposalWithProject[]>([]);
  const [selectedChat, setSelectedChat] = useState<{
    projectId: string;
    providerId: string;
    providerName: string;
  } | null>(null);

  // Major European countries for display
  const countries = [
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'BE', name: 'Belgium' }
  ];

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscription
        const subscriptionDoc = await getDoc(doc(db, 'subscriptions', currentUser.uid));
        if (subscriptionDoc.exists()) {
          const data = subscriptionDoc.data() as UserSubscription;
          setSubscription({
            ...data,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate()
          });
        }

        // Fetch user's proposals
        const proposalsRef = collection(db, 'proposals');
        const proposalsQuery = query(
          proposalsRef,
          where('providerId', '==', currentUser.uid),
          where('status', '==', 'accepted')
        );
        
        const proposalsSnapshot = await getDocs(proposalsQuery);
        const proposalsWithProjects: ProposalWithProject[] = [];

        // Fetch project data for each accepted proposal
        for (const proposalDoc of proposalsSnapshot.docs) {
          const proposalData = proposalDoc.data();
          
          try {
            const projectRef = doc(db, 'projects', proposalData.projectId);
            const projectSnap = await getDoc(projectRef);
            
            if (projectSnap.exists()) {
              const projectData = projectSnap.data();
              
              // Check for messages from requester
              let hasRequesterMessage = false;
              try {
                const chatId = `${proposalData.projectId}_${currentUser.uid}`;
                const messagesRef = collection(db, `projects/${proposalData.projectId}/chats/${chatId}/messages`);
                const messagesQuery = query(
                  messagesRef,
                  where('senderId', '==', projectData.userId)
                );
                
                const messagesSnap = await getDocs(messagesQuery);
                hasRequesterMessage = !messagesSnap.empty;
              } catch (chatError) {
                console.warn('Could not check for requester messages:', chatError);
              }

              const proposalWithProject: ProposalWithProject = {
                id: proposalDoc.id,
                ...proposalData,
                createdAt: proposalData.createdAt.toDate(),
                updates: proposalData.updates?.map((update: any) => ({
                  ...update,
                  createdAt: update.createdAt.toDate()
                })) || [],
                hasRequesterMessage,
                project: {
                  id: projectSnap.id,
                  ...projectData,
                  createdAt: projectData.createdAt.toDate(),
                  updatedAt: projectData.updatedAt.toDate()
                } as Project
              };

              proposalsWithProjects.push(proposalWithProject);
            }
          } catch (error) {
            console.warn('Error fetching project for proposal:', proposalDoc.id, error);
          }
        }

        // Separate ongoing and closed projects
        const ongoing = proposalsWithProjects.filter(p => 
          p.project.status === 'active' || p.project.status === 'upcoming'
        );
        const closed = proposalsWithProjects.filter(p => 
          p.project.status === 'closed' || p.project.status === 'completed' || p.project.status === 'archived'
        );

        // Sort by project creation date (descending)
        ongoing.sort((a, b) => b.project.createdAt.getTime() - a.project.createdAt.getTime());
        closed.sort((a, b) => b.project.createdAt.getTime() - a.project.createdAt.getTime());

        setOngoingProjects(ongoing);
        setClosedProjects(closed);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, t]);

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return t('project.timeline1');
      case '2-3':
        return t('project.timeline2-3');
      case '3-6':
        return t('project.timeline3-6');
      case '6+':
        return t('project.timeline6+');
      default:
        return t('project.timelineUnknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'archived':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const openChat = (projectId: string, providerId: string, providerName: string) => {
    setSelectedChat({ projectId, providerId, providerName });
  };

  const closeChat = () => {
    setSelectedChat(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">{t('dashboard.provider')}</h1>

      {/* Subscription Status */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <CreditCard className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-lg font-semibold">{t('dashboard.currentPlan')}</h2>
              {subscription ? (
                <div className="mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    subscription.tier === 'normal'
                      ? 'bg-gray-100 text-gray-800'
                      : subscription.tier === 'gold'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {t(`businessPlan.${subscription.tier}`)}
                  </span>
                  <p className="text-sm text-gray-600 mt-2">
                    {t('dashboard.validUntil')}: {subscription.endDate.toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 mt-1">{t('dashboard.noSubscription')}</p>
              )}
            </div>
          </div>
          <Link
            to="/business-plan"
            className="bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors flex items-center"
          >
            {subscription ? t('dashboard.changePlan') : t('businessPlan.selectPlan')}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Ongoing Projects */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            {t('language') === 'fr' ? 'Projets en cours' : 'Ongoing Projects'} ({ongoingProjects.length})
          </h2>
        </div>

        {ongoingProjects.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              {t('language') === 'fr' ? 'Aucun projet en cours' : 'No ongoing projects'}
            </p>
            <Link
              to="/marketplace"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('marketplace.browseProjects')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ongoingProjects.map((proposal) => (
              <div key={proposal.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium">{proposal.project.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.project.status)}`}>
                    {t(`project.${proposal.project.status}`)}
                  </span>
                </div>

                <p className="text-gray-600 mb-4 line-clamp-2">{proposal.project.shortDescription}</p>

                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{getTimelineLabel(proposal.project.timeline)}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{countries.find(c => c.code === proposal.project.country)?.name || proposal.project.country}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{t('project.created')}: {format(proposal.project.createdAt, 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    <span>{proposal.project.views} {t('project.views')}</span>
                  </div>
                </div>

                {/* Message notification */}
                {proposal.hasRequesterMessage && (
                  <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <div className="flex items-center text-primary-700">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">
                        {t('language') === 'fr' ? 'Nouveau message du demandeur' : 'New message from requester'}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <Link
                    to={`/project/${proposal.project.id}`}
                    className="flex-1 text-center py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors text-sm"
                  >
                    {t('project.viewDetails')}
                  </Link>
                  <button
                    onClick={() => openChat(proposal.project.id, currentUser!.uid, userData?.username || 'Provider')}
                    className={`flex-1 py-2 rounded-md text-sm transition-colors flex items-center justify-center ${
                      proposal.hasRequesterMessage
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    {t('chat.chat')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Closed Projects */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold flex items-center">
            <XCircle className="h-5 w-5 text-gray-600 mr-2" />
            {t('language') === 'fr' ? 'Projets terminés' : 'Closed Projects'} ({closedProjects.length})
          </h2>
        </div>

        {closedProjects.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {t('language') === 'fr' ? 'Aucun projet terminé' : 'No closed projects'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {closedProjects.map((proposal) => (
              <div key={proposal.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow opacity-75">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium">{proposal.project.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.project.status)}`}>
                    {t(`project.${proposal.project.status}`)}
                  </span>
                </div>

                <p className="text-gray-600 mb-4 line-clamp-2">{proposal.project.shortDescription}</p>

                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{getTimelineLabel(proposal.project.timeline)}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{countries.find(c => c.code === proposal.project.country)?.name || proposal.project.country}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{t('project.created')}: {format(proposal.project.createdAt, 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center">
                    <Send className="h-4 w-4 mr-1" />
                    <span>{t('proposal.accepted')}: {format(proposal.createdAt, 'MMM d, yyyy')}</span>
                  </div>
                </div>

                <Link
                  to={`/project/${proposal.project.id}`}
                  className="block w-full text-center py-2 border border-gray-400 text-gray-600 rounded-md hover:bg-gray-50 transition-colors text-sm"
                >
                  {t('project.viewDetails')}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          projectId={selectedChat.projectId}
          chatId={`${selectedChat.projectId}_${selectedChat.providerId}`}
          otherUserId={ongoingProjects.find(p => p.project.id === selectedChat.projectId)?.project.userId || ''}
          otherUserName="Project Requester"
          onClose={closeChat}
        />
      )}
    </div>
  );
};

export default ProviderDashboard;